import classNames from 'classnames';
import {
    get as _get,
    has as _has,
    isArray as _isArray,
    intersectionWith as _intersectionWith,
    isBoolean as _isBoolean,
    isEmpty as _isEmpty,
    isEqual as _isEqual,
    isNil as _isNil,
    isObject as _isObject,
    isString as _isString,
    isFunction as _isFunction,
    sortBy as _sortBy,
} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router';
import {Table as SemanticTable, Menu, Icon, Dropdown, Checkbox} from 'semantic-ui-react';

import {Alert, CircularProgress, Backdrop} from '@ui';
import {
    getParamFromSearchUrl,
    setParamsToSearchUrl,
    urlSearchStringToObject,
} from '@utils/helpers';
import {showMessageBox} from '@utils/messageBox';
import {TABLE_DATA_LIMIT} from '@constants/variables';
import {ASC, DESC, LIMIT_PER_PAGE_LIST, TABLE_SORT_PARAMS_STRATEGIES} from '@constants/tableComponentConfig';
import {setTableParamsToStore} from '@actions';
import TableRow from '@appComponents/TableComponents/Row';
import MessageBox from '@appComponents/MessageBox';

import appConfig from '../../../avcmpConfig';

/**
 * @ignore
 */
export class Table extends React.Component {
    static propTypes = {
        adjustHeight: PropTypes.func,
        className: PropTypes.string,
        columns: PropTypes.object.isRequired,
        data: PropTypes.array,
        dataOriginal: PropTypes.array,
        dataLimitWarning: PropTypes.number,
        defaultSort: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        editable: PropTypes.bool,
        getSelectedElements: PropTypes.func,
        isCompact: PropTypes.bool,
        limit: PropTypes.number,
        limitMessage: PropTypes.string,
        loading: PropTypes.bool,
        match: PropTypes.object,
        multiple: PropTypes.bool,
        name: PropTypes.string.isRequired,
        noDataText: PropTypes.string,
        noMessageBox: PropTypes.bool,
        pagination: PropTypes.bool,
        rowClassname: PropTypes.func,
        rowOnClick: PropTypes.func,
        rowRenderer: PropTypes.func,
        rowRendererProps: PropTypes.object,
        selectable: PropTypes.bool,
        selected: PropTypes.array,
        showLimitWarning: PropTypes.bool,
        size: PropTypes.string,
        structured: PropTypes.bool,
        setTableParamsToStore: PropTypes.func.isRequired,
        url: PropTypes.string,
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
            search: PropTypes.string,
        }),
        defaultSortDirection: PropTypes.oneOf([ASC, DESC]),
        tableParams: PropTypes.shape({
            limit: PropTypes.number,
            order: PropTypes.oneOf([ASC, DESC]),
            orderBy: PropTypes.string,
            page: PropTypes.number,
        }),
    };

    static defaultProps = {
        className: null,
        defaultSort: null,
        getSelectedElements: () => null,
        isCompact: true,
        noDataText: 'No data found',
        rowRendererProps: null,
        size: 'small',
        structured: false,
    };

    constructor(props) {
        super(props);

        let data = [];

        if (props.data && 0 < props.data.length) {
            data = props.data;
        }

        const showLimitWarning = this.props?.showLimitWarning ?? false,
            limit = this.getLimitPerPage(),
            initialSortState = {
                column: this.getOrderBy(),
                direction: this.getOrder(),
            },
            initialSortParams = this.resolveSortParamsStrategy(
                props.location?.pathname ?? window.location.pathname,
                props.match.path,
                initialSortState
            );

        this.state = {
            url: props.url || props.match.path,
            column: initialSortParams.orderBy,
            direction: initialSortParams.order,
            page: this.getPage(),
            limit: limit,
            data: data,
            dataReceived: [],
            dataOriginal: [],
            showLimitWarning: showLimitWarning,
            selected: this.props.selected || [],
            maxPageCount: 1,
            listIndexUpperLimit: 0,
            preventSetTableState: false,
        };

        initialSortParams.orderBy && setParamsToSearchUrl({orderBy: initialSortParams.orderBy});
        initialSortParams.order && setParamsToSearchUrl({order: initialSortParams.order});

        if (limit && limit !== appConfig.app.defaultLimitItemsOnList) {
            setParamsToSearchUrl({pageLimit: limit});
        }

        showLimitWarning && data.length >= props.dataLimitWarning && this.showLimitWarningBox(props);

        if (this.props.getSelectedElements) {
            this.props.getSelectedElements(this.state.selected);
        }
    }

    getPage = () => (
        (getParamFromSearchUrl('page', 'int') ?? this.props?.tableParams?.page) || 1
    );

    getOrderBy = () => {
        const orderBy = getParamFromSearchUrl('orderBy') ?? this.props?.tableParams?.orderBy;

        return _has(this.props, `columns.${orderBy}`) ? orderBy : null;
    };

    getOrder = () => {
        const order = getParamFromSearchUrl('order') ?? this.props?.tableParams?.order;

        return (ASC === order || DESC === order) ? order : null;
    };

    getLimitPerPage = () => (
        getParamFromSearchUrl('pageLimit', 'int')
            || this.props?.tableParams?.limit
            || appConfig.app.defaultLimitItemsOnList
    );

    parseValueForSort = (value) => {
        let sortValue = value;

        if (_has(value, 'name')) {
            sortValue = value?.name ?? null;
        } else if (_isArray(value)) {
            sortValue = value.join('-');
        } else if ('' === value || undefined === value) {
            sortValue = null;
        }

        if (_isString(sortValue)) {
            sortValue = sortValue.toLowerCase();
        } else if (_isObject(sortValue)) {
            sortValue = null;
        }

        return sortValue;
    };

    getSortValue = (valueA, valueB, sortDirection) => {
        if ((null === valueB) && !_isBoolean(valueB)) {
            return -1;
        }

        if ((null === valueA) && !_isBoolean(valueA)) {
            return 1;
        }

        return this.getSortValueDirection(valueA, valueB, sortDirection);
    };

    getSortValueDirection = (valueA, valueB, sortDirection) => {
        if (valueA > valueB) {
            return (DESC === sortDirection) ? -1 : 1;
        }

        if (valueA < valueB) {
            return (DESC === sortDirection) ? 1 : -1;
        }

        return 0;
    };

    /**
     * Method to sort tables
     * should be passed to every sorting column
     *
     * Rules:
     * 1) When row[column] is a string or object with 'name' property pass "sorting: true"
     * 2) When row[column] is an object specify sorting function which returns the same string
     *  which is displayed in a cell
     * 3) Use getParsedId function from helpers.js when possible
     */
    sortData = (column, data, sort = null) => {
        if (!data) {
            return [];
        } else if (!column) {
            return Array.from(data);
        }

        const sortDirection = sort || this.state.direction,
            dataToSort = Array.from(data);

        const sortedData = [...dataToSort].sort((a, b) => {
            let valueA = a[column],
                valueB = b[column];

            if (this.props.columns[column] && 'function' === typeof (this.props.columns[column].sorting)) {
                valueA = this.props.columns[column].sorting(a);
                valueB = this.props.columns[column].sorting(b);
            }

            valueA = this.parseValueForSort(valueA);
            valueB = this.parseValueForSort(valueB);

            return this.getSortValue(valueA, valueB, sortDirection);
        });

        if (DESC === sortDirection) {
            const nonEmpty = sortedData.filter(
                el => null !== el[column] && el[column] !== undefined && !_isBoolean(el[column])
            );

            for (let i = 0; i < nonEmpty.length; i++) {
                sortedData[i] = nonEmpty[i];
            }
        }

        return sortedData;
    };

    handleSort = (column, updateUrl = true) => {
        if (!column) {
            if (!this.state.column && this.props.defaultSort) {
                if (_isFunction(this.props.defaultSort)) {
                    this.setState(() => ({
                        data: _sortBy(this.state.data, [this.props.defaultSort]),
                    }));

                    return;
                }

                this.handleSort(this.props.defaultSort, false);
            } else if (this.state.column && updateUrl) {
                this.setParamsToSearchUrlFromState();
            }
        } else {
            let direction = ASC;

            if (column === this.state.column) {
                direction = (DESC === this.state.direction) ? ASC : DESC;
            } else if (column === this.props.defaultSort && this.props.defaultSortDirection) {
                direction = this.props.defaultSortDirection;
            }

            this.setState((state) => ({
                column: column,
                direction: direction,
                data: this.sortData(column, state.data, direction),
            }), () => {
                this.props.setTableParamsToStore({
                    orderBy: column,
                    order: direction,
                });

                if (updateUrl) {
                    setParamsToSearchUrl({
                        orderBy: column,
                        order: direction,
                        ...this.getAdditionalTableSearchParams(),
                    });
                }

                const adjustHeight = this.getAdjustHeight();

                if (adjustHeight) adjustHeight();
            });
        }
    };

    getAdditionalTableSearchParams = () => {
        const additionalParams = {};

        if (1 < this.state.page) {
            additionalParams.page = this.state.page;
        }

        if (appConfig.app.defaultLimitItemsOnList !== this.state.limit) {
            additionalParams.pageLimit = this.state.limit;
        }

        return additionalParams;
    };

    resolveSortParamsStrategy = (pathName, matchPath, state = this.state) => {
        const context = { state };
        const strategy = TABLE_SORT_PARAMS_STRATEGIES[pathName]
            ?? TABLE_SORT_PARAMS_STRATEGIES[matchPath]
            ?? TABLE_SORT_PARAMS_STRATEGIES.default;

        return strategy(context);
    };

    setParamsToSearchUrlFromState = () => {
        const pathName = window.location.pathname;
        const sortParams = this.resolveSortParamsStrategy(pathName, this.props.match.path);

        setParamsToSearchUrl({
            ...sortParams,
            ...this.getAdditionalTableSearchParams(),
        });
    };

    toggleRows = () => {
        const toggledRows = [];

        if (this.state.selected.length !== this.state.data.length) {
            this.state.data.forEach((row) => {
                toggledRows.push(row.id);
            });
        }

        this.setState(() => ({
            preventSetTableState: true,
            selected: toggledRows,
        }), () => {
            if (this.props.getSelectedElements) {
                this.props.getSelectedElements(toggledRows);
            }
        });
    };

    renderHeaderRow = () => {
        const headerContent = [];
        let selectAllCheckbox = null;

        if (this.props.multiple) {
            const intersection = _intersectionWith(
                this.state.data,
                this.state.selected,
                (data, selectedId) => (selectedId === data.id)
            );

            selectAllCheckbox = (
                <Checkbox
                    className='--checkbox-small'
                    onClick={this.toggleRows}
                    checked={this.state.data.length === intersection.length}
                    indeterminate={
                        0 < intersection.length
                        && intersection.length !== this.state.data.length
                    }
                />
            );
        }

        if (this.props.selectable) {
            headerContent.push(
                <SemanticTable.HeaderCell key={'select-all'} textAlign='center'>
                    {selectAllCheckbox}
                </SemanticTable.HeaderCell>
            );
        }

        Object.keys(this.props.columns).forEach((columnKey) => {
            const column = this.props.columns[columnKey],
                isSortingColumn = (undefined !== column.sorting && column.sorting),
                rowProps = {};
            let sortIcon = null;

            if (columnKey === this.state.column) {
                const iconDirection = (DESC === this.state.direction) ? 'down' : 'up';
                sortIcon = <Icon className={`table__sort triangle ${iconDirection}`}/>;
            } else {
                if (isSortingColumn) {
                    sortIcon = <Icon className='table__sort sort --unordered'/>;
                }
            }

            const textAlign = this.getSettingForColumn(columnKey, 'header.align'),
                isCollapsed = this.getSettingForColumn(columnKey, 'header.isCollapsed'),
                isHidden = this.getSettingForColumn(columnKey, 'isHidden', false),
                headerClassName = this.getSettingForColumn(columnKey, 'header.className', ''),
                width = this.getSettingForColumn(columnKey, 'width'),
                className = classNames(sortIcon ? '--sortable' : '--unsortable', headerClassName);

            if (!isHidden) {
                headerContent.push(
                    <SemanticTable.HeaderCell
                        textAlign={textAlign}
                        collapsing={isCollapsed}
                        width={width || undefined}
                        key={this.props.name + columnKey}
                        {...rowProps}
                        onClick={(isSortingColumn) ? () => this.handleSort(columnKey) : null}
                        className={className}
                    >
                        <div className='table__header-cell-wrapper'>
                            <span className='table__header-name'>
                                {'function' === typeof column.label ? column.label() : column.label}
                                {this.renderLabelForColumn(column.subLabel)}
                                {this.renderLabelForColumn(column.subSubLabel)}
                            </span>
                            {sortIcon}
                        </div>
                    </SemanticTable.HeaderCell>
                );
            }
        });

        return (
            headerContent
        );
    };

    selectRow = (data) => {
        if (this.props.rowOnClick !== undefined) {
            this.props.rowOnClick(data);
        }
    };

    onChangeCheckbox = (event, data) => {
        const indexOfElement = this.state.selected.indexOf(data['data-id']);
        let selectedElements = [...this.state.selected];

        if (undefined !== this.props.multiple && this.props.multiple) {
            if (0 <= indexOfElement) {
                selectedElements.splice(indexOfElement, 1);
            } else {
                selectedElements.push(data['data-id']);
            }
        } else {
            selectedElements = [data['data-id']];
        }

        if (this.props.getSelectedElements) {
            this.props.getSelectedElements(selectedElements);
        }

        this.setState(() => ({
            preventSetTableState: true,
            selected: selectedElements,
        }));
    };

    renderLabelForColumn = (label) => {
        if (!label) return null;

        return (
            <span>
                <br />
                {label}
            </span>
        );
    };

    getSettingForColumn = (columnKey, path, defaultValue = null) => (
        _get(this.props, `columns.${columnKey}.${path}`, defaultValue)
    );

    getPaginationRange = () => {
        const totalLength = this.state.data.length;

        if (false === this.props.pagination) {
            return {startIndex: 0, limitForLoop: totalLength};
        }

        const startIndex = (this.state.page * this.state.limit - this.state.limit);
        let limitForLoop = startIndex + this.state.limit;

        if (limitForLoop > totalLength) {
            limitForLoop = totalLength;
        }

        return {startIndex, limitForLoop};
    };

    renderSelectCell = (rowItem, subRowsCount) => {
        const selectCellProps = {
            key: `select-${rowItem.id}`,
            textAlign: 'center',
        };

        if (0 < subRowsCount) {
            selectCellProps.rowSpan = subRowsCount + 1;
        }

        return (
            <SemanticTable.Cell {...selectCellProps}>
                <Checkbox
                    checked={0 <= this.state.selected.indexOf(rowItem.id)}
                    className='--checkbox-small'
                    data-id={rowItem.id}
                    onChange={this.onChangeCheckbox}
                />
            </SemanticTable.Cell>
        );
    };

    resolveCellContent = (columnKey, rowData) => {
        let cellContent = null;

        if ('function' === typeof (this.props.rowRenderer)) {
            cellContent = this.props.rowRenderer(columnKey, rowData, this.props.rowRendererProps);
        }

        if (null === cellContent) {
            cellContent = rowData[columnKey];

            if (_isObject(cellContent)) {
                cellContent = cellContent?.name ?? '';
            }
        }

        if (cellContent === undefined) {
            cellContent = <div>Missing settings for this cell</div>;
        }

        return cellContent;
    };

    buildRowCells = (rowData, options) => {
        const {keyBuilder, classNameContext, skipMissingProps = false} = options;
        const cells = [];

        Object.keys(this.props.columns).forEach((columnKey) => {
            if (skipMissingProps && !Object.hasOwn(rowData, columnKey)) {
                return;
            }

            const isHidden = this.getSettingForColumn(columnKey, 'isHidden', false),
                isHiddenCell = rowData[columnKey]?.hidden || false;

            if (isHidden || isHiddenCell) {
                return;
            }

            const cellContent = this.resolveCellContent(columnKey, rowData);
            const textAlign = this.getSettingForColumn(columnKey, 'content.align'),
                singleLine = this.getSettingForColumn(columnKey, 'content.singleLine'),
                className = this.getSettingForColumn(columnKey, 'className', ''),
                width = this.getSettingForColumn(columnKey, 'width'),
                rowSpan = (rowData[columnKey]?.rowSpan) ? rowData[columnKey].rowSpan : 1;

            cells.push(
                <SemanticTable.Cell
                    className={('function' === typeof className) ? className(classNameContext) : className}
                    key={keyBuilder(columnKey)}
                    singleLine={singleLine}
                    textAlign={textAlign}
                    width={width || undefined}
                    rowSpan={rowSpan}
                >
                    {cellContent}
                </SemanticTable.Cell>
            );
        });

        return cells;
    };

    getRowClassNames = (rowItem) => {
        const rowClassNames = [];

        if (rowItem.is_disabled) {
            rowClassNames.push('--is-disabled');
        }

        if (rowItem.is_archived) {
            rowClassNames.push('--is-archived');
        }

        if (true === _isFunction(this.props.rowClassname)) {
            rowClassNames.push(this.props.rowClassname(rowItem));
        }

        return rowClassNames;
    };

    getTableRowKey = (rowItem, index) => {
        const idPart = rowItem.id?.value ?? rowItem.id;

        return this.props.name + idPart + index;
    };

    renderDataRows = () => {
        const data = this.state.data;

        if (!data?.length) {
            return null;
        }

        const dataRows = [];
        const {startIndex, limitForLoop} = this.getPaginationRange();

        for (let i = startIndex; i < limitForLoop; i++) {
            const rowItem = data[i],
                subRows = rowItem.subRows || [],
                dataRow = [];

            if (this.props.selectable) {
                dataRow.push(this.renderSelectCell(rowItem, subRows.length));
            }

            dataRow.push(...this.buildRowCells(rowItem, {
                keyBuilder: (columnKey) => i + columnKey,
                classNameContext: rowItem,
            }));

            const rowClassNames = this.getRowClassNames(rowItem),
                tableRowKey = this.getTableRowKey(rowItem, i);

            dataRows.push(
                <TableRow
                    className={rowClassNames.join(' ')}
                    key={tableRowKey}
                    data={rowItem}
                    onClick={this.selectRow}
                >
                    {dataRow}
                </TableRow>
            );

            subRows.forEach((subRow, subIdx) => {
                const subDataRow = this.buildRowCells(subRow, {
                    keyBuilder: (columnKey) => `${i}-sub-${subIdx}-${columnKey}`,
                    classNameContext: rowItem,
                    skipMissingProps: true,
                });

                dataRows.push(
                    <TableRow
                        className={rowClassNames.join(' ')}
                        key={`${tableRowKey}-sub-${subIdx}`}
                        data={rowItem}
                        onClick={this.selectRow}
                    >
                        {subDataRow}
                    </TableRow>
                );
            });
        }

        return dataRows;
    };

    changePage = (page, setUrl = true) => {
        const urlQueryObject = urlSearchStringToObject(window.location.search);

        this.props.setTableParamsToStore({page: page});

        this.setState((state, props) => ({
            page: page,
            listIndexUpperLimit: (state.limit * page <= props.data.length)
                ? state.limit * page : props.data.length,
        }), this.getAdjustHeight());

        urlQueryObject.page = page;

        setUrl && setParamsToSearchUrl(urlQueryObject, true);
    };

    changePageDropdownAction = (event, data) => {
        this.changePage(data.value);
    };

    changePageButton = (event, data) => {
        this.changePage(data.index);
    };

    getAdjustHeight = () => (
        _isFunction(this.props.adjustHeight) ? this.props.adjustHeight : null
    );

    changeLimit = (event, data) => {
        const limit = data.value,
            maxPageCount = Math.ceil(this.state.data.length / limit),
            page = this.getPage(),
            tableParamsForUrl = {
                ...urlSearchStringToObject(window.location.search),
                pageLimit: limit,
            };

        this.props.setTableParamsToStore({
            page: page,
            limit: limit,
        });

        if (1 !== page && 1 < maxPageCount) {
            tableParamsForUrl.page = page > maxPageCount ? maxPageCount : page;
        }

        tableParamsForUrl.pageLimit = limit;

        setParamsToSearchUrl(tableParamsForUrl, true);

        this.setState((state, props) => ({
            limit: limit,
            maxPageCount: maxPageCount,
            listIndexUpperLimit: (limit <= props.data.length) ? limit : props.data.length,
        }), this.getAdjustHeight());
    };

    setTableState = () => {
        const {page, limit, order, orderBy} = this.getUrlTableParams();

        this.setState((state, props) => {
            if (state.preventSetTableState) {
                return {preventSetTableState: false};
            } else {
                const dataLength = (undefined === props.data || null === props.data) ? 1 : props.data.length,
                    sortedData = this.sortData(orderBy, props.data, order),
                    maxPageCount = Math.ceil(dataLength / limit);

                return {
                    data: sortedData,
                    dataOriginal: props.data,
                    selected: (
                        _isEqual(state.dataOriginal, props.data)
                        && _isEqual(props.selected, this.props.selected)
                    )
                        ? state.selected
                        : (props.selected || []),
                    page: ((maxPageCount < page) ? maxPageCount : page),
                    limit: limit,
                    direction: order,
                    column: orderBy,
                    maxPageCount: maxPageCount,
                    listIndexUpperLimit: (state.limit * state.page <= dataLength)
                        ? state.limit * state.page : dataLength,
                };
            }
        }, this.handleSort);
    };

    componentDidMount() {
        this.setTableState();
    }

    getUrlTableParams = () => {
        const nonStatePage = this.getPage(),
            nonStateLimit = this.getLimitPerPage();

        return {
            page: (nonStatePage !== this.state.page) ? nonStatePage : this.state.page,
            limit: (nonStateLimit !== this.state.limit) ? nonStateLimit: this.state.limit,
            orderBy: this.getOrderBy() ?? this.state.column,
            order: this.getOrder() ?? this.state.direction,
        };
    };

    componentDidUpdate(prevProps, prevState) {
        if (this.props !== prevProps) {
            if (this.props.loading
                || (
                    _isArray(this.props.data)
                    && _isArray(prevProps.data)
                    && this.props.data.length !== prevProps.data.length)
            ) {
                this.setState(() => ({
                    showLimitWarning: true,
                }));
            }

            if (prevState.url === this.props.match.path
                || !_isEqual(prevState.dataOriginal, this.props.data)
            ) {
                this.setTableState();
            }

            if (
                prevProps.loading
                && !this.props.loading
                && this.props.getSelectedElements
            ) {
                const selected = this.props.selected || [];
                this.setState(() => ({selected: selected}));
                this.props.getSelectedElements(selected);
            }

            if (prevState.showLimitWarning && prevProps.showLimitWarning) {
                this.showLimitWarningBox(this.props);
            }
        }
    }

    showLimitWarningBox = (props) => {
        if (!props.data) {
            return;
        }

        const limitData = props?.dataLimitWarning ?? TABLE_DATA_LIMIT,
            dataArray = props?.data ?? [],
            limitMessage = props?.limitMessage ?? `The number of results displayed is limited to ${limitData}. Please use filters to narrow your search.`;

        if ((!dataArray ? [] : dataArray).length >= limitData && !props.loading) {
            showMessageBox(
                this.props.name + '_warningBox',
                null,
                limitMessage,
                'error',
                true
            );

            this.setState(() => ({
                showLimitWarning: false,
            }));
        }
    };

    renderPaginatorPageButtons = () => {
        const pageButtons = [],
            pageNumberList = [],
            recordFrom = (1 < this.state.page) ? ((this.state.page - 1) * this.state.limit + 1) : 1;

        let recordTo = this.state.page * this.state.limit;

        if (this.state.data.length <= (this.state.page * this.state.limit)) {
            recordTo = this.state.data.length;
        } else if (1 === this.state.page) {
            recordTo = this.state.limit;
        }

        pageButtons.push(
            <div className='page-info' key={`${this.props.name}-pageinfo`}>
                <strong>{`${recordFrom} - ${recordTo} of ${this.state.data.length}`}</strong>
            </div>
        );

        pageButtons.push(
            <div key={`${this.props.name}-limit-change-dropdown`}>
                <Dropdown
                    onChange={this.changeLimit}
                    value={this.state.limit} text={`Limit: ${this.state.limit}`}
                    item
                    scrolling
                    options={LIMIT_PER_PAGE_LIST}
                    upward
                />
            </div>
        );

        if (this.state.data.length < this.state.limit) {
            return pageButtons;
        }

        for (let i = 0; i < this.state.maxPageCount; i++) {
            pageNumberList[i] = {key: i + 1, value: i + 1, text: i + 1};
        }

        pageButtons.push(
            <div key={`${this.props.name}-page-change-dropdown`}>
                <Dropdown
                    onChange={this.changePageDropdownAction}
                    value={this.state.page} text={`Page: ${this.state.page}`}
                    item
                    button
                    scrolling
                    options={pageNumberList}
                    className={'pagination__page-change-dropdown'}
                />
            </div>
        );

        if (1 !== this.state.page) {
            if (1 < this.state.maxPageCount) {
                pageButtons.push(
                    <Menu.Item as='a' index={1} key={`${this.props.name}-first-page`} icon
                        onClick={this.changePageButton}>
                        <Icon name='angle double left'/>
                    </Menu.Item>
                );
            }

            pageButtons.push(
                <Menu.Item as='a' index={this.state.page - 1} key={`${this.props.name}-previous-page`} icon
                    onClick={this.changePageButton}>
                    <Icon name='angle left'/>
                </Menu.Item>
            );
        }

        for (let i = this.state.page - 3; i < this.state.page; i++) {
            if (0 < i) {
                pageButtons.push(
                    <Menu.Item as='a' index={i} key={`${this.props.name}-page-${i}`}
                        onClick={this.changePageButton}>{i}</Menu.Item>
                );
            }
        }

        pageButtons.push(
            <Menu.Item as='a' key={`${this.props.name}-page-${this.state.page}`} disabled>
                {this.state.page}
            </Menu.Item>
        );

        for (let i = this.state.page; i < this.state.page + 3 && i < this.state.maxPageCount; i++) {
            pageButtons.push(
                <Menu.Item key={`${this.props.name}-page-${i + 1}`} as='a' index={i + 1}
                    onClick={this.changePageButton}>
                    {i + 1}
                </Menu.Item>
            );
        }

        if (this.state.page < this.state.maxPageCount) {
            pageButtons.push(
                <Menu.Item
                    key={`${this.props.name}-page-next`}
                    as='a'
                    index={this.state.page + 1}
                    icon
                    onClick={this.changePageButton}
                >
                    <Icon name='angle right'/>
                </Menu.Item>
            );

            pageButtons.push(
                <Menu.Item
                    as='a'
                    key={`${this.props.name}-page-last`}
                    icon
                    index={this.state.maxPageCount}
                    onClick={this.changePageButton}
                >
                    <Icon name='angle double right'/>
                </Menu.Item>
            );
        }

        return pageButtons;
    };

    renderPaginationContainer = () => (
        <SemanticTable.HeaderCell
            colSpan={Object.keys(this.props.columns).length + (true === this.props.selectable ? 1 : 0)}>
            <Menu floated='right' pagination>
                {this.renderPaginatorPageButtons()}
            </Menu>
        </SemanticTable.HeaderCell>
    );

    renderTable = () => {
        return (
            <div>
                <MessageBox name={this.props.name + '_warningBox'}/>
                <div className='table-container'>
                    <SemanticTable
                        compact={this.props.isCompact}
                        size={this.props.size}
                        sortable
                        celled
                        striped
                        selectable
                        structured={this.props.structured}
                        className={this.props.className}
                    >
                        <SemanticTable.Header>
                            <SemanticTable.Row>
                                {this.renderHeaderRow()}
                            </SemanticTable.Row>
                        </SemanticTable.Header>
                        <SemanticTable.Body>
                            {this.renderDataRows()}
                        </SemanticTable.Body>
                        {
                            false === this.props.pagination
                                ? null
                                : <SemanticTable.Footer>
                                    <SemanticTable.Row>
                                        {this.renderPaginationContainer()}
                                    </SemanticTable.Row>
                                </SemanticTable.Footer>
                        }
                    </SemanticTable>
                </div>
            </div>
        );
    };

    renderNoDataMessage = () => {
        if (this.props.noMessageBox) {
            return (
                <div>{this.props.noDataText}</div>
            );
        } else {
            return (
                <div className='table-container'>
                    <Alert icon={false}>
                        <h4>
                            {this.props.noDataText}
                        </h4>
                    </Alert>
                </div>
            );
        }
    };

    render = () => {
        const {data, loading} = this.props;

        if (loading) {
            return (
                <Backdrop open inverted>
                    <CircularProgress />
                </Backdrop>
            );
        } else if (_isNil(data) || _isEmpty(data)) {
            return this.renderNoDataMessage();
        } else {
            return this.renderTable();
        }
    };
}

const mapDispatchToProps = (dispatch, props) => {
    return {
        setTableParamsToStore: (params) => {
            return dispatch(setTableParamsToStore({tableParams: {...params}, name: props.name}));
        },
    };
};

const mapStateToProps = (state, props) => {
    return {
        tableParams: state.app?.table[props.name],
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Table));
